import { Goal, Proposal } from "creso-core/types";
import {
  calculateExpectedCorpus,
  calculateFutureValue,
  calculateTotalSipInvested,
  formatAsPercentage,
  getProposalStepUp,
} from "creso-core/utils";
import { ChartContainer, cn, getPieMinAngle } from "web-design-system";
import { Pie, PieChart, Cell } from "recharts";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { formatDuration } from "@/modules/planning/config";
import Amount from "@funds/components/atoms/amount";
import { GoalIcon } from "web-core";
import {
  buildChartConfig,
  CORPUS_BREAKDOWN_COLORS,
} from "./breakdown-pie-chart";
import { StepUpLabel } from "./step-up-label";

interface GoalOverviewSectionProps {
  proposal: Proposal;
  goalData: Goal | undefined;
  isGoalLinked: boolean;
}

export function GoalOverviewSection({
  proposal,
  goalData,
  isGoalLinked,
}: GoalOverviewSectionProps) {
  const totalMonthlySip = Number(proposal.total_sip_amount);
  const totalLumpsum = Number(proposal.total_lumpsum_amount);
  const durationInMonths = Number(proposal.duration_months ?? 0);
  const stepUp = getProposalStepUp(
    proposal,
    proposal.proposal_funds.filter((f) => f.type === "SIP").length
  );
  const totalSipInvested = calculateTotalSipInvested(
    totalMonthlySip,
    durationInMonths,
    stepUp
  );
  const totalInvested = totalSipInvested + totalLumpsum;

  const expectedCorpus = calculateExpectedCorpus(
    totalMonthlySip,
    totalLumpsum,
    Number(proposal.expected_return || 12),
    durationInMonths,
    stepUp
  );

  const linkedHoldingsValue = isGoalLinked
    ? Number(goalData?.total_current_value ?? 0)
    : 0;

  // Linked holdings aren't part of expectedCorpus (that only covers lumpsum
  // + SIP), so their growth is computed separately and folded into the
  // returns segment.
  const linkedHoldingsGrowth =
    linkedHoldingsValue > 0
      ? Math.max(
          Math.round(
            calculateFutureValue(
              linkedHoldingsValue,
              Number(proposal.expected_return || 12),
              durationInMonths
            )
          ) - linkedHoldingsValue,
          0
        )
      : 0;

  const returns =
    Math.max(expectedCorpus - totalInvested, 0) + linkedHoldingsGrowth;
  const totalExpectedCorpus =
    expectedCorpus + linkedHoldingsValue + linkedHoldingsGrowth;

  const showCorpusBreakdown = isGoalLinked || !!proposal.projected_value;

  const corpusBreakdownData = [
    linkedHoldingsValue > 0 && {
      name: "Linked holdings",
      value: linkedHoldingsValue,
      fill: CORPUS_BREAKDOWN_COLORS.linkedHoldings,
    },
    {
      name: "One-time investment",
      value: totalLumpsum,
      fill: CORPUS_BREAKDOWN_COLORS.oneTime,
    },
    {
      name: "Total SIPs",
      value: totalSipInvested,
      fill: CORPUS_BREAKDOWN_COLORS.sip,
    },
    {
      name: "Total expected returns",
      value: returns,
      fill: CORPUS_BREAKDOWN_COLORS.returns,
    },
  ].filter(Boolean) as { name: string; value: number; fill: string }[];

  const corpusBreakdownConfig = buildChartConfig(corpusBreakdownData);

  return (
    <div className="flex print:h-[calc(100vh-150px)] flex-col break-before-page">
      <div className="flex flex-row mb-6 items-center gap-3">
        <GoalIcon category={goalData?.category ?? "CUSTOM"} />
        <span className="text-2xl font-semibold">
          {proposal?.name ?? "Proposal for Wealth Creation"}
        </span>
      </div>

      {/* Investment Summary */}
      <div className="grid grid-cols-6 pb-6 gap-4">
        <div className="col-span-2 border border-border justify-around bg-card rounded-xl flex flex-row flex-1 p-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Duration</span>
            <span className="text-base font-semibold">
              {formatDuration(durationInMonths)}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Annual Returns
            </span>
            <span className="text-base font-semibold">
              {formatAsPercentage(proposal.expected_return, "-", 1)}
              &nbsp;(Expected)
            </span>
          </div>
        </div>
        <div className="flex border border-border col-span-4 bg-card rounded-xl justify-around flex-row gap-10 p-5 flex-1">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Total invested
            </span>
            <span className="text-base font-semibold">
              <Amount precision={0}>{totalInvested}</Amount>
            </span>
          </div>
          {isGoalLinked && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">
                Linked holdings
              </span>
              <span className="text-base font-semibold">
                <Amount precision={0}>
                  {goalData?.total_current_value ?? 0}
                </Amount>
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">One-time</span>
            <span className="text-base font-semibold">
              <Amount precision={0}>{proposal.total_lumpsum_amount}</Amount>
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Monthly SIP</span>
            <span className="text-base font-semibold">
              <Amount precision={0}>{proposal.total_sip_amount}</Amount>
            </span>
            <StepUpLabel proposal={proposal} />
          </div>
        </div>
      </div>

      <ConditionalRenderer condition={showCorpusBreakdown}>
        <div className="p-10 border border-border rounded-lg bg-card flex flex-row items-center gap-40">
          <ChartContainer
            config={corpusBreakdownConfig}
            className="w-90 h-90 shrink-0"
          >
            <PieChart>
              <Pie
                data={corpusBreakdownData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={130}
                paddingAngle={2}
                cornerRadius={4}
                minAngle={getPieMinAngle(55, 80)}
                isAnimationActive={false}
              >
                {corpusBreakdownData.map((entry, index) => (
                  <Cell key={`corpus-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>

          <div className="flex flex-col px-8 flex-1">
            <div className="flex flex-col gap-6">
              {corpusBreakdownData.map((item) => (
                <div
                  key={item.name}
                  className="flex justify-between items-center"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-1 h-4 rounded-full"
                      style={{ backgroundColor: item.fill }}
                    />
                    <span className="text-sm text-muted-foreground">
                      {item.name}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      item.fill === CORPUS_BREAKDOWN_COLORS.returns &&
                        "text-green-700"
                    )}
                  >
                    <Amount precision={0}>{item.value}</Amount>
                  </span>
                </div>
              ))}
            </div>

            <div className="h-px bg-border my-5" />

            <div className="flex justify-between items-center">
              <span className="text-base font-bold">Expected corpus</span>
              <span className="text-base font-bold">
                <Amount precision={0}>{totalExpectedCorpus}</Amount>
              </span>
            </div>
          </div>
        </div>
      </ConditionalRenderer>

      <span className="text-sm mt-8 text-muted-foreground leading-relaxed">
        <Amount precision={0}>{totalInvested}</Amount>&nbsp; of your goal comes
        from your contributions, and&nbsp;
        <Amount precision={0}>{returns}</Amount> from returns{" "}
        {showCorpusBreakdown
          ? `(assuming ${formatAsPercentage(proposal.expected_return, "-", 1)} p.a.
        over ${formatDuration(durationInMonths)}).`
          : ""}
        <br />
        Please review these assumptions carefully and reach out with any
        questions, it’s important that you’re confident and clear before
        investing.
      </span>
    </div>
  );
}
